import { ReactComponent as Cable } from '@assets/icons/cable.svg';
import { ReactComponent as CircleDollarSign } from '@assets/icons/circle-dollar-sign.svg';
import { ReactComponent as BrainCircuit } from '@assets/icons/brain-circuit.svg';
import { ReactComponent as Business } from '@assets/icons/briefcase-business.svg';
import { ReactComponent as Landmark } from '@assets/icons/landmark.svg';
import { ReactComponent as UserIcon } from '@assets/icons/user-cog.svg';
import { ReactComponent as PiggyBank } from '@assets/icons/piggy-bank.svg';
import GridViewOutlined from '@mui/icons-material/GridViewOutlined';
import { SUBMODULE_TYPE } from '@constants/constants';
import { NAV_ROUTES } from '@constants/navRoutes';
import { titles } from '@utils/translations/en';
import { NavGroupIcon, SidebarGroupType } from './types';

export enum FRONTEND_SUBMODULE_TYPE {
  DEAL_SETUP_PAGE = 'DEAL_SETUP_PAGE',
  SELF_SERVE_REQUESTS = 'SELF_SERVE_REQUESTS',
  USER_MANAGEMENT = 'USER_MANAGEMENT',
}

export const ADMIN_ONLY_SUBMODULES: {
  type: FRONTEND_SUBMODULE_TYPE;
  label: string;
  path: string;
}[] = [
  {
    type: FRONTEND_SUBMODULE_TYPE.DEAL_SETUP_PAGE,
    label: 'Deal Setup',
    path: NAV_ROUTES.DEALS,
  },
  {
    type: FRONTEND_SUBMODULE_TYPE.SELF_SERVE_REQUESTS,
    label: 'Self Serve Requests',
    path: NAV_ROUTES.DASHBOARD_REQUESTS,
  },
  {
    type: FRONTEND_SUBMODULE_TYPE.USER_MANAGEMENT,
    label: titles.USERS,
    path: NAV_ROUTES.USER_MANAGEMENT,
  },
];

export type NavSubmoduleType = SUBMODULE_TYPE | FRONTEND_SUBMODULE_TYPE;

// Icon for top-level modules that sit outside the groups
export const UNGROUPED_MODULE_ICON: NavGroupIcon = GridViewOutlined;

export interface NavModuleConfig {
  // true: module row with its submodules nested under it.
  // false/omitted: submodules shown directly under the group.
  nested?: boolean;
  // Module row title when nested; otherwise renames the submodules
  label?: string;
  submoduleTypes: NavSubmoduleType[];
}

export interface NavGroupConfig {
  groupType: SidebarGroupType;
  label: string;
  icon: NavGroupIcon;
  modules: NavModuleConfig[];
}

export const NAVIGATION_CONFIG: NavGroupConfig[] = [
  {
    groupType: SidebarGroupType.DATA_CONNECTION,
    label: 'Data Connection',
    icon: Cable,
    modules: [
      { submoduleTypes: [SUBMODULE_TYPE.GETTING_STARTED] },
      { submoduleTypes: [SUBMODULE_TYPE.DUE_DILIGENCE] },
      { submoduleTypes: [SUBMODULE_TYPE.FINANCIAL_REPORTING] },
    ],
  },
  {
    groupType: SidebarGroupType.UA_FINANCING,
    label: 'UA Financing',
    icon: CircleDollarSign,
    modules: [
      { submoduleTypes: [SUBMODULE_TYPE.FUNDING_ELIGIBILITY] },
      { submoduleTypes: [SUBMODULE_TYPE.FINANCING_REPORT_SUMMARY] },
      { submoduleTypes: [SUBMODULE_TYPE.FUNDING_REQUEST] },
      { submoduleTypes: [SUBMODULE_TYPE.DOWNLOADABLE_REPORTS] },
      { submoduleTypes: [SUBMODULE_TYPE.ALERTS_DASHBOARD] },
      { submoduleTypes: [SUBMODULE_TYPE.REFERRER_SETUP] },
      { submoduleTypes: [SUBMODULE_TYPE.FUNDING_REQUEST_TRACKER] },
      { submoduleTypes: [SUBMODULE_TYPE.FINANCIAL_REPORTING_DASHBOARD] },
      { submoduleTypes: [FRONTEND_SUBMODULE_TYPE.DEAL_SETUP_PAGE] },
    ],
  },
  {
    groupType: SidebarGroupType.DATA_INTELLIGENCE,
    label: 'Data Intelligence',
    icon: BrainCircuit,
    modules: [
      { submoduleTypes: [SUBMODULE_TYPE.COHORT_ANALYSIS] },
      { submoduleTypes: [SUBMODULE_TYPE.MARKET_SCAN] },
      { submoduleTypes: [SUBMODULE_TYPE.BENCHMARKS_CUSTOM] },
      {
        nested: true,
        label: 'Roas Projections',
        submoduleTypes: [SUBMODULE_TYPE.MODEL_REQUESTS],
      },
      {
        submoduleTypes: [
          SUBMODULE_TYPE.FINANCIAL_PROJECTIONS_V2,
          SUBMODULE_TYPE.FINANCIAL_PROJECTIONS,
        ],
      },
      { submoduleTypes: [SUBMODULE_TYPE.SPEND_IMPACT] },
      { submoduleTypes: [SUBMODULE_TYPE.THRESHOLD_BREACH_ALERTS] },
      {
        nested: true,
        label: 'PvX Report',
        submoduleTypes: [
          SUBMODULE_TYPE.INVESTOR_REPORT,
          SUBMODULE_TYPE.INVESTOR_REPORT_V2,
          SUBMODULE_TYPE.INVESTOR_REPORT_V2_EXT,
        ],
      },
    ],
  },
  {
    groupType: SidebarGroupType.SALES_ORIGINATION,
    label: 'Sales/Origination',
    icon: Business,
    modules: [
      { submoduleTypes: [SUBMODULE_TYPE.MARKET_INTELLIGENCE] },
      { submoduleTypes: [SUBMODULE_TYPE.EXPOSURE_FORECASTING] },
      { submoduleTypes: [SUBMODULE_TYPE.COMMISSIONS_DASHBOARD] },
      { submoduleTypes: [SUBMODULE_TYPE.DEAL_SETUP_DASHBOARD] },
      { submoduleTypes: [FRONTEND_SUBMODULE_TYPE.SELF_SERVE_REQUESTS] },
      { submoduleTypes: [SUBMODULE_TYPE.SELF_SERVE] },
      { submoduleTypes: [SUBMODULE_TYPE.IMPACT_REPORT] },
      { submoduleTypes: [SUBMODULE_TYPE.LAMBDA_INTELLIGENCE] },
    ],
  },
  {
    groupType: SidebarGroupType.PVX_CAPITAL,
    label: 'PvX Capital',
    icon: Landmark,
    modules: [
      { submoduleTypes: [SUBMODULE_TYPE.GAME_PERFORMANCE] },
      { submoduleTypes: [SUBMODULE_TYPE.INVESTMENT_PERFORMANCE] },
      { submoduleTypes: [SUBMODULE_TYPE.ACCOUNTING_REVENUE] },
      {
        nested: true,
        label: 'Bank Transfers',
        submoduleTypes: [SUBMODULE_TYPE.COMMISSIONS_PAYOUT, SUBMODULE_TYPE.COMMISSIONS_PAYINS],
      },
    ],
  },
  {
    groupType: SidebarGroupType.PVX_CONSUMER_GROWTH_FUND,
    label: 'PvX Consumer Growth Fund',
    icon: PiggyBank,
    modules: [
      { submoduleTypes: [SUBMODULE_TYPE.FUND_PERFORMANCE] },
      { submoduleTypes: [SUBMODULE_TYPE.FUND_ALLOCATION] },
      { submoduleTypes: [SUBMODULE_TYPE.LP_SETUP] },
    ],
  },
  {
    groupType: SidebarGroupType.ADMINISTRATOR,
    label: 'Administrator',
    icon: UserIcon,
    modules: [
      { submoduleTypes: [FRONTEND_SUBMODULE_TYPE.USER_MANAGEMENT] },
      { submoduleTypes: [SUBMODULE_TYPE.CLIENT_USER_MANAGEMENT] },
    ],
  },
];
